import { useState, type AnimationEvent as ReactAnimationEvent, type FormEvent as ReactFormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { FormError } from '@/components/ui/form-error';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { cn } from '@/lib/utils';
import { organizationCodeRule, loginPasswordRule, requiredText, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField } from '@/lib/form-validation';
import { getSafeRedirectPath } from '@/lib/redirect';

const schema = z.object({
  organizationCode: organizationCodeRule,
  uniqueId: requiredText('Unique ID', 50, 'Unique ID is required.'),
  password: loginPasswordRule,
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const { login } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Browser autofill writes the input value directly to the DOM, bypassing
  // react-hook-form state. Chrome fires an `animationstart` named `-webkit-autofill`
  // (Firefox/Safari may not), so we also patch values from the DOM at submit time.
  const syncAutofilledValue = (event: ReactAnimationEvent<HTMLInputElement>) => {
    const animation = event.animationName;
    if (!/autofill|auto-?fill/i.test(animation)) return;
    const input = event.currentTarget;
    const name = input.name as keyof FormData;
    if (!(name in schema.shape)) return;
    setValue(
      name,
      name === 'organizationCode' ? input.value.trim().toUpperCase() : input.value,
      { shouldValidate: false }
    );
  };

  const handleFormSubmit = (event: ReactFormEvent<HTMLFormElement>) => {
    for (const name of Object.keys(schema.shape) as (keyof FormData)[]) {
      if (getValues(name)) continue;
      const input = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (input && input.value) {
        setValue(
          name,
          name === 'organizationCode' ? input.value.trim().toUpperCase() : input.value,
          { shouldValidate: false }
        );
      }
    }
    void handleSubmit(onSubmit, onInvalid)(event);
  };

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await login(data.organizationCode, data.uniqueId, data.password);
      addToast('Welcome back!', 'success');
      navigate(getSafeRedirectPath(searchParams.get('redirect'), '/dashboard'));
    } catch (err: any) {
      addToast(err.message || 'Login failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="login-card">
        <div className="login-brand">
          <img src="/assests/SS Logo_RGB.png" alt="Departmental Labour App" className="login-logo" />
          <h1>Departmental Labour App</h1>
          <p>Construction Labour Management</p>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>Sign In</CardTitle>
            <CardDescription>Enter your credentials to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <Label htmlFor="organizationCode">Organization ID</Label>
                <Input
                  id="organizationCode"
                  type="text"
                  placeholder="SWAYAM001"
                  autoComplete="organization"
                  onAnimationStart={syncAutofilledValue}
                  {...register('organizationCode', { setValueAs: (v: string) => v.trim().toUpperCase() })}
                  aria-invalid={errors.organizationCode ? true : undefined}
                  aria-describedby={errors.organizationCode ? 'organizationCode-error' : undefined}
                  className={cn(errors.organizationCode && 'border-error')}
                />
                <FormError id="organizationCode-error" message={errors.organizationCode?.message} />
              </div>
              <div>
                <Label htmlFor="uniqueId">Unique ID</Label>
                <Input
                  id="uniqueId"
                  type="text"
                  placeholder="e.g. ENG001"
                  autoComplete="username"
                  onAnimationStart={syncAutofilledValue}
                  {...register('uniqueId')}
                  aria-invalid={errors.uniqueId ? true : undefined}
                  aria-describedby={errors.uniqueId ? 'uniqueId-error' : undefined}
                  className={cn(errors.uniqueId && 'border-error')}
                />
                <FormError id="uniqueId-error" message={errors.uniqueId?.message} />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-brand-500 hover:underline">Forgot?</Link>
                </div>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  onAnimationStart={syncAutofilledValue}
                  {...register('password')}
                  aria-invalid={errors.password ? true : undefined}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  className={cn(errors.password && 'border-error')}
                />
                <FormError id="password-error" message={errors.password?.message} />
              </div>
              <Button type="submit" className="w-full" loading={loading}>
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted mt-6">
          Dev credentials: ADMIN / Admin@12345
        </p>
      </div>
    </PublicLayout>
  );
}
